<template>
  <o-dropdown v-if="reservations" ref="dropdown">
    <o-button flat size="sm">
      {{ $t('components.reservation.create.recents.label') }}
    </o-button>

    <template #content>
      <div
        v-for="reservation in reservations"
        :key="reservation.idReservation"
        class="w-full flex items-center justify-between px-3 text-sm py-2 leading-none space-x-4"
      >
        <div>
          <p class="mb-1">
            {{ reservation.services.map(({ service }) => service.title).join(', ') }}
          </p>
          <p class="opacity-70">
            {{ reservation.services.map(({ employee }) => employee.displayName).join(', ') }}
          </p>
        </div>

        <o-button size="xs" variant="primary" icon="reservationRebook" @click="handleRebook(reservation)">
          {{ $t('reservation.rebook') }}
        </o-button>
      </div>
    </template>
  </o-dropdown>
</template>

<script setup lang="ts">
import {
  ODropdown,
  ODropdownItem,
} from '@/components/elements'

const { $api } = useNuxtApp()

interface Props {
  staging: Record<string, any>
}

interface Emits {
  (e: 'update:staging', value: any): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const reservations = ref<any[] | null>(null)
const dropdown = ref<any>()
let latestReservationsRequest = 0

const _staging = computed({
  get () {
    return props.staging
  },
  set (value) {
    emit('update:staging', value)
  },
})

async function fetchReservations () {
  const requestId = ++latestReservationsRequest
  const customer = _staging.value.customer
  const idCustomer = customer?.idCustomer || customer?.id

  if (!idCustomer) {
    reservations.value = null
    return
  }

  try {
    const result = await $api.customer.pastReservations(String(idCustomer))

    if (requestId !== latestReservationsRequest) return

    if (result) {
      const reversed = result.items.slice().reverse()

      const ids = reversed
        .map((reservation: any) => {
          const services = reservation.services
          const id = services.map(({ service }: any) => service.idService)
          return id.join('-')
        })

      const unique = reversed
        .filter((reservation: any, index: number) => {
          const services = reservation.services
          const id = services.map(({ service }: any) => service.idService)

          return !ids.includes(id.join('-'), index + 1)
        })

      const items = unique.reverse()

      items.length = Math.min(items.length, 5)

      reservations.value = items
    } else {
      reservations.value = null
    }
  } catch (e) {
    if (requestId !== latestReservationsRequest) return

    console.log(e)
    reservations.value = null
  }
}

function handleRebook (reservation: any) {
  props.staging.fromReservation(reservation)

  dropdown.value.close()
}

watch(() => _staging.value.customer, () => {
  fetchReservations()
})

onMounted(() => {
  fetchReservations()
})
</script>
